class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isStickyAlways = this.dataset.stickyAlways === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    // Optional CSS selector for the section over which the header should hide
    this.hideOverSelector = this.dataset.hideOverSelector || null;

    this.classBodyAlwaysMobileMenu = "mobile-nav";

    this.header = document.querySelector(".page-header");
    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );
    this.desktopMenuBar = document.querySelector(".wt-drawer--nav");

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";
    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";
  }

  connectedCallback() {
    this.init();
    if (typeof window.syncPageHeaderTop === "function") {
      window.syncPageHeaderTop();
    }
  }

  disconnectedCallback() {
    this.disableStickyHeader();
  }

  getHeaderHeight() {
    return this.header ? this.header.offsetHeight : 0;
  }

  /** Returns the target element for the hide-over behavior (if any). */
  getHideOverTarget() {
    if (!this.hideOverSelector) return null;
    if (this._hideOverTarget && document.body.contains(this._hideOverTarget)) {
      return this._hideOverTarget;
    }
    this._hideOverTarget =
      document.querySelector(this.hideOverSelector) || null;
    return this._hideOverTarget;
  }

  /**
   * Should the sticky header be hidden because its bottom edge reached
   * the target section's top edge?
   *
   * Geometry reference (viewport coordinates):
   * - header top: usually 0 when sticky is shown
   * - header bottom: header.offsetHeight
   * - section top: target.getBoundingClientRect().top
   *
   * We hide when sectionTop <= headerBottom.
   */
  shouldHideOverSection() {
    const target = this.getHideOverTarget();
    if (!target || !this.header) return false;

    const rect = target.getBoundingClientRect();
    const headerH = this.getHeaderHeight();

    // Small hysteresis to avoid flicker on exact boundary (in px)
    const EPS = 1;

    // True when the section intersects with the header band [0, headerH)
    // i.e. sectionTop < headerBottom && sectionBottom > 0
    const overlapsHeaderBand = rect.top < headerH - EPS && rect.bottom > EPS;

    return overlapsHeaderBand;
  }

  /** Attach / detach a lightweight resize handler to keep things in sync. */
  attachResizeHandler() {
    // Re-run calculations on resize; cheap logic, no throttling needed here
    this._onResize = () => {
      // Force potential reflow-based checks in next scroll tick
      // so currentScrollPos logic stays consistent.
      this.scrollHandler?.();
    };
    window.addEventListener("resize", this._onResize, { passive: true });
  }

  detachResizeHandler() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
      this._onResize = null;
    }
  }

  enableStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    document.body.classList.add("page-header-sticky");

    let prevScrollpos = window.pageYOffset;

    const isDesktop = window.matchMedia("(min-width: 1200px)").matches;
    const isMenuBarOpen = () =>
      this.header.classList.contains(this.desktopHeaderWithMenuBarClass);
    const isHeaderWithDesktopNav =
      !document.body.classList.contains("mobile-nav");
    const allLLevelsLinks =
      this.desktopMenuBar?.querySelectorAll("a[data-menu-level]");
    const onlyLevel1Links = this.desktopMenuBar?.querySelectorAll(
      "a[data-menu-level='1']",
    );

    const header = document.querySelector("#header");

    const calculateNavbarTopMargin = () => {
      const navbar = document.querySelector("#wt-drawer-nav");
      if (!navbar || !header) return 0;

      let marginTop = 0;

      if (navbar.offsetHeight > header.offsetHeight) {
        marginTop = header.offsetHeight - navbar.offsetHeight;
      } else {
        marginTop = Math.abs(navbar.offsetHeight - header.offsetHeight);
      }

      navbar.style.setProperty("--top-margin", `${marginTop}px`);

      return marginTop;
    };

    const calculateStickyFiltersTopOffset = (value = 0) => {
      const stickyFilters = document.querySelector(
        ".collection__sticky-header",
      );
      const plpWrapper = document.querySelector(".collection-grid-section");

      if (stickyFilters && plpWrapper) {
        const offset = value ?? `${this.header.offsetHeight}px`;
        plpWrapper.style.setProperty("--filters-sticky-offset", offset);
      }
    };

    calculateNavbarTopMargin();
    calculateStickyFiltersTopOffset();

    const stickyHeader = {
      show: () => {
        if (this.header) this.header.classList.add(this.showClass);
        stickyHeader.visible = true;
        stickyHeader.handleBehavior();
        calculateNavbarTopMargin();
        calculateStickyFiltersTopOffset();
      },
      hide: () => {
        if (this.header) this.header.classList.remove(this.showClass);
        stickyHeader.visible = false;
        stickyHeader.handleBehavior();
        calculateStickyFiltersTopOffset(0);
      },
      enable: () => {
        if (this.header) this.header.classList.add(this.enabledClass);
        stickyHeader.enabled = true;
        stickyHeader.handleBehavior();
      },
      disable: () => {
        if (this.header)
          this.header.classList.remove(this.enabledClass, this.showClass);
        stickyHeader.enabled = false;
        stickyHeader.handleBehavior();
      },
      enabled: false,
      visible: true,
      handleBehavior: () => {
        if (isHeaderWithDesktopNav && isDesktop && this.header) {
          stickyHeader.log();
          if (!isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "-1");
          }
          if (isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "0");
          }
          if (!stickyHeader.enabled) {
            setTabindex(onlyLevel1Links, "0");
            if (this.desktopMenuTrigger)
              setTabindex([this.desktopMenuTrigger], "-1");
          } else if (this.desktopMenuTrigger)
            setTabindex([this.desktopMenuTrigger], "0");
        }
      },
      log: () => {},
    };

    // --- Scroll handler with "hide-over-section" override ----------
    this.scrollHandler = () => {
      const currentScrollPos = window.pageYOffset;

      // If we're over the target section, force-hide regardless of isStickyAlways.
      if (this.shouldHideOverSection()) {
        stickyHeader.hide();
      } else {
        // Original behavior preserved
        if (!this.isStickyAlways) {
          if (prevScrollpos > currentScrollPos) {
            stickyHeader.show();
          } else {
            stickyHeader.hide();
          }
        } else {
          stickyHeader.show();
        }
      }

      prevScrollpos = currentScrollPos;
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    this.attachResizeHandler();

    this.desktopMenuTrigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.desktopMenuBar?.classList.toggle("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.toggle(
        "wt-header__sticky-menu-trigger--active",
      );
      this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);
      stickyHeader.handleBehavior();
    });

    const sentinel = document.querySelector(".sticky-header__threshold");
    const handleStickySentinel = (entries) => {
      entries.forEach(({ isIntersecting }) => {
        if (isIntersecting) {
          stickyHeader.disable();
        } else {
          stickyHeader.enable();
        }
      });
    };

    this.stickyHeaderObserver = new IntersectionObserver(handleStickySentinel, {
      root: null,
      rootMargin: `${this.isStickyAlways ? "-160" : "-100"}px 0px 0px 0px`,
      threshold: 0,
    });
    if (sentinel) this.stickyHeaderObserver.observe(sentinel);
  }

  disableStickyHeader() {
    if (this.header) {
      // Remove classes added by sticky header
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
      document.body.classList.remove("page-header-sticky");
      this.desktopMenuBar?.classList.remove("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.remove(
        "wt-header__sticky-menu-trigger--active",
      );
    }

    // Remove the scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }

    // Detach resize handler
    this.detachResizeHandler();

    // Disconnect the IntersectionObserver
    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }
  }

  init() {
    if (this.isSticky) {
      this.enableStickyHeader();
    } else {
      this.disableStickyHeader();
    }
  }
}

/**
 * Fixed .page-header top:
 * - Announcement visible in viewport: height + gap (17 mobile / 15 desktop)
 * - Announcement hidden or scrolled away: default (17 mobile / 30 desktop)
 *
 * Uses IntersectionObserver so close + scroll-away both reset to default.
 */
const pageHeaderTopMQ = window.matchMedia("(max-width: 767px)");
let pageHeaderAnnouncementVisible = false;
let pageHeaderLastTop = "";
let pageHeaderIntersectionObserver = null;

function getAnnouncementBarEl() {
  return (
    document.querySelector("announcement-bar.wt-announcement-bar") ||
    document.querySelector("announcement-bar")
  );
}

function getAnnouncementSectionEl(bar) {
  return (
    bar?.closest(".shopify-section, .wt-announcement") ||
    document.querySelector("#shopify-section-announcement-bar") ||
    document.querySelector(".shopify-section.wt-announcement")
  );
}

function isAnnouncementDomVisible(bar) {
  if (!bar || !document.contains(bar)) return false;
  if (bar.style.display === "none" || bar.hasAttribute("hidden")) return false;

  const section = getAnnouncementSectionEl(bar);
  if (section?.style.display === "none") return false;

  const barStyle = window.getComputedStyle(bar);
  if (barStyle.display === "none" || barStyle.visibility === "hidden") {
    return false;
  }
  if (section) {
    const sectionStyle = window.getComputedStyle(section);
    if (
      sectionStyle.display === "none" ||
      sectionStyle.visibility === "hidden"
    ) {
      return false;
    }
  }

  return (bar.offsetHeight || 0) > 0;
}

function applyPageHeaderTop(announcementInView) {
  const isMobile = pageHeaderTopMQ.matches;
  const gap = isMobile ? 17 : 15;
  const defaultTop = isMobile ? 17 : 30;
  const bar = getAnnouncementBarEl();
  const domVisible = isAnnouncementDomVisible(bar);
  const announcementHeight = domVisible ? Math.round(bar.offsetHeight || 0) : 0;
  const isPdpMobile =
    isMobile && document.body.classList.contains("template-product");

  // Class + height only for PDP mobile (<768px)
  document.body.classList.toggle(
    "announcement-bar-visible",
    isPdpMobile && announcementHeight > 0,
  );
  document.documentElement.style.setProperty(
    "--announcement-bar-height",
    isPdpMobile && announcementHeight > 0 ? `${announcementHeight}px` : "0px",
  );

  const useAnnouncementOffset = announcementHeight > 0 && announcementInView;
  const height = useAnnouncementOffset ? announcementHeight : 0;
  const top = height > 0 ? height + gap : defaultTop;
  const next = `${top}px`;

  pageHeaderLastTop = next;
  document.documentElement.style.setProperty("--page-header-top", next);
  document.documentElement.classList.add("page-header-top-ready");
}

function syncPageHeaderTop() {
  const bar = getAnnouncementBarEl();
  if (!isAnnouncementDomVisible(bar)) {
    pageHeaderAnnouncementVisible = false;
    applyPageHeaderTop(false);
    return;
  }

  if (pageHeaderIntersectionObserver == null) {
    const rect = bar.getBoundingClientRect();
    pageHeaderAnnouncementVisible =
      rect.bottom > 0 && rect.top < window.innerHeight;
  }

  applyPageHeaderTop(pageHeaderAnnouncementVisible);
}

function bindAnnouncementVisibility() {
  const bar = getAnnouncementBarEl();
  const section = getAnnouncementSectionEl(bar);

  if (pageHeaderIntersectionObserver) {
    pageHeaderIntersectionObserver.disconnect();
    pageHeaderIntersectionObserver = null;
  }

  if (typeof IntersectionObserver !== "undefined" && bar) {
    pageHeaderIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        pageHeaderAnnouncementVisible = !!(
          entry &&
          entry.isIntersecting &&
          entry.intersectionRatio > 0
        );
        if (!isAnnouncementDomVisible(bar)) {
          pageHeaderAnnouncementVisible = false;
        }
        applyPageHeaderTop(pageHeaderAnnouncementVisible);
      },
      {
        root: null,
        threshold: [0, 0.01, 1],
      },
    );
    pageHeaderIntersectionObserver.observe(bar);
  }

  if (window.__pageHeaderAnnouncementMutationObserver) {
    window.__pageHeaderAnnouncementMutationObserver.disconnect();
  }

  if (typeof MutationObserver !== "undefined" && (bar || section)) {
    window.__pageHeaderAnnouncementMutationObserver = new MutationObserver(
      () => {
        syncPageHeaderTop();
      },
    );
    const opts = {
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    };
    if (bar) {
      window.__pageHeaderAnnouncementMutationObserver.observe(bar, opts);
    }
    if (section && section !== bar) {
      window.__pageHeaderAnnouncementMutationObserver.observe(section, opts);
    }
  }

  syncPageHeaderTop();
}

function initPageHeaderTopSync() {
  if (window.__pageHeaderTopSyncInitialized) {
    syncPageHeaderTop();
    return;
  }
  window.__pageHeaderTopSyncInitialized = true;

  bindAnnouncementVisibility();

  window.addEventListener("resize", syncPageHeaderTop, { passive: true });
  if (typeof pageHeaderTopMQ.addEventListener === "function") {
    pageHeaderTopMQ.addEventListener("change", syncPageHeaderTop);
  }

  document.addEventListener("announcement-bar:change", () => {
    pageHeaderAnnouncementVisible = false;
    syncPageHeaderTop();
    requestAnimationFrame(syncPageHeaderTop);
  });

  document.addEventListener(
    "click",
    (event) => {
      if (!event.target.closest(".wt-announcement__close")) return;
      pageHeaderAnnouncementVisible = false;
      requestAnimationFrame(() => {
        syncPageHeaderTop();
        requestAnimationFrame(syncPageHeaderTop);
      });
    },
    true,
  );

  document.addEventListener("shopify:section:load", bindAnnouncementVisibility);
  document.addEventListener("shopify:section:unload", () => {
    pageHeaderAnnouncementVisible = false;
    syncPageHeaderTop();
  });
  document.addEventListener("shopify:section:reorder", syncPageHeaderTop);
}

window.syncPageHeaderTop = syncPageHeaderTop;

customElements.define("page-header", PageHeaderSection);
initPageHeaderTopSync();
